import { deepClone } from "@mixitone/mvc/src/utils/deepClone";
import { SingleEliminationFormat } from "./SingleEliminationFormat";
import {
  type EntryDataField,
  type FormatSchema,
  type SchemaSettings,
  type StandingsData,
  type StandingsEntry,
  TournamentFormat,
} from "./TournamentFormat";
import {
  type Entry,
  type EntryId,
  type Match,
  type Round,
  type TournamentState,
  type Winner,
  blankTournamentState,
  matchHasEntries,
  matchKey,
  matchMetadata,
  matchResolved,
  matchVoided,
} from "./types";

const schema = {
  numGroups: {
    type: "number",
    description: "Number of groups",
    default: 1,
    minimum: 1,
    maximum: 128,
  },
  pointsForWin: {
    type: "number",
    description: "Points awarded for a win",
    default: 2,
  },
  pointsForDraw: {
    type: "number",
    description: "Points awarded for a draw",
    default: 1,
  },
  pointsForLoss: {
    type: "number",
    description: "Points awarded for a loss",
    default: 0,
  },
  enablePlayoffs: {
    type: "boolean",
    description: "Enable playoffs after group stage",
    default: false,
  },
  qualifiersPerGroup: {
    type: "number",
    description: "Number of entries from each group that advance to playoffs",
    default: 1,
    minimum: 1,
    maximum: 8,
  },
  divisions: {
    type: "number",
    description: "Number of divisions for playoffs",
    default: 1,
    minimum: 1,
    maximum: 8,
  },
  divisionSplittingRules: {
    type: "string",
    description: "Division splitting rules (JSON)",
    default: "",
    hidden: true,
  },
} satisfies FormatSchema;

/**
 * Round Robin tournament format implementation
 *
 * In this format:
 * 1. Players are divided into groups of configurable size
 * 2. Within each group, every player plays against all other players
 * 3. Final standings are determined by total points
 *
 * When playoffs are enabled:
 * 4. Top performers from each group advance to a single elimination playoff
 *
 * This implementation supports storing group information in both:
 * - Match metadata: For backward compatibility with existing tests
 * - Competition metadata: For persistence between transformations
 *
 * When using the TournamentDataAdapter, group assignments and standings
 * are stored in the competition's format_data column, ensuring consistent
 * group assignments even if individual matches are modified.
 */
export class RoundRobinFormat extends TournamentFormat<typeof schema> {
  getName(): string {
    return "Round Robin";
  }

  get description(): string {
    return "Players are divided into groups where everyone plays against everyone else, with optional playoffs";
  }

  getEntryLimits(): { min: number; max: number } {
    return {
      min: 2,
      max: 128,
    };
  }

  getSettingsSchema() {
    return schema;
  }

  getEntryDataSchema(): EntryDataField[] {
    return [
      {
        key: "group",
        label: "Group",
        type: "string" as const,
        description: "The group this entry belongs to",
        sortable: true,
      },
      {
        key: "points",
        label: "Points",
        type: "number" as const,
        description: "Total points earned",
        sortable: true,
      },
      {
        key: "qualified",
        label: "Qualified",
        type: "boolean" as const,
        description: "Whether this entry qualified for playoffs",
        sortable: true,
      },
      {
        key: "phase",
        label: "Phase",
        type: "string" as const,
        description: "Current tournament phase for this entry",
        sortable: true,
      },
      {
        key: "division",
        label: "Division",
        type: "number" as const,
        description: "Playoff division after group stage (editable until playoffs start)",
        sortable: true,
      },
      {
        key: "games",
        label: "Games",
        type: "string" as const,
        description: "Games played",
        sortable: false,
        value: (metadata) => {
          metadata.wins = metadata.wins || 0;
          metadata.losses = metadata.losses || 0;
          metadata.draws = metadata.draws || 0;
          return `Won: ${metadata.wins}, Lost: ${metadata.losses}, Draw: ${metadata.draws}`;
        },
      },
    ];
  }

  getMatchDataSchema(): EntryDataField[] {
    return [
      {
        key: "group",
        label: "Group",
        type: "string" as const,
        description: "The group this match belongs to",
        sortable: true,
      },
      {
        key: "phase",
        label: "Phase",
        type: "string" as const,
        description: "Tournament phase (group or playoff)",
        sortable: true,
      },
      {
        key: "division",
        label: "Division",
        type: "number" as const,
        description: "Division this match belongs to",
        sortable: true,
      },
    ];
  }

  transform(state?: TournamentState, settings?: SchemaSettings<typeof schema>): TournamentState {
    // If no state provided, return empty state
    if (!state) {
      return blankTournamentState();
    }

    // Get entries from round 0
    const { entries } = state;

    // For 0 entries, return empty state
    if (entries.length === 0) {
      return blankTournamentState();
    }

    // Create a new tournament state starting with the entries
    const newState: TournamentState = deepClone(state);

    // For 1 entry, just return the original state
    if (entries.length === 1) {
      return newState;
    }

    // Get settings with defaults
    const numGroups = settings?.numGroups ?? schema.numGroups.default;
    const pointsForWin = settings?.pointsForWin ?? schema.pointsForWin.default;
    const pointsForDraw = settings?.pointsForDraw ?? schema.pointsForDraw.default;
    const pointsForLoss = settings?.pointsForLoss ?? schema.pointsForLoss.default;
    const enablePlayoffs = settings?.enablePlayoffs ?? schema.enablePlayoffs.default;
    const qualifiersPerGroup = settings?.qualifiersPerGroup ?? schema.qualifiersPerGroup.default;

    // Initialize metadata if needed
    if (!newState.metadata) {
      newState.metadata = {};
    }

    if (enablePlayoffs) {
      // Handle round robin with playoffs format
      const divisions = settings?.divisions ?? schema.divisions.default;
      return this.transformWithPlayoffs(
        newState,
        numGroups,
        pointsForWin,
        pointsForDraw,
        pointsForLoss,
        qualifiersPerGroup,
        divisions,
        settings,
      );
    }

    // Handle standard round robin format
    return this.transformRoundRobin(newState, numGroups, pointsForWin, pointsForDraw, pointsForLoss);
  }

  /**
   * Transform the tournament state using the standard round robin format
   */
  private transformRoundRobin(
    state: TournamentState,
    numGroups: number,
    pointsForWin: number,
    pointsForDraw: number,
    pointsForLoss: number,
  ): TournamentState {
    const newState = deepClone(state);

    // Initialize rounds array if needed
    if (!newState.rounds) {
      newState.rounds = [];
    }

    // Remove any empty matches
    for (let i = 0; i < newState.rounds.length; i++) {
      newState.rounds[i] = newState.rounds[i]
        ? newState.rounds[i].filter((match) => {
            return Boolean(match[0]) && Boolean(match[1]);
          })
        : [];
    }

    // Store groups in a local variable
    let groups: Record<string, EntryId[]>;

    // Check if matches are in progress - if so, preserve existing groups to avoid disrupting the tournament
    const matchesInProgress = this.areMatchesInProgress(newState.rounds);
    const groupsLocked = newState.metadata?.groupsLocked === true;
    const existingGroups = this.copyExistingGroups(
      newState.metadata?.groups,
      new Set(newState.entries.map((entry) => entry[0])),
    );
    const existingGroupCount = Object.keys(existingGroups).length;

    if (groupsLocked && existingGroupCount > 0) {
      // Locked: keep the printed groups. Manual entry.group changes still apply.
      groups = existingGroups;
      this.applyManualGroupAssignments(groups, newState.entries);
    } else if (existingGroupCount > 0) {
      const existingGroupCountMismatched = existingGroupCount !== numGroups;

      if (matchesInProgress || !existingGroupCountMismatched) {
        groups = existingGroups;
        this.assignUnassignedEntriesToGroups(groups, newState.entries);
      } else {
        // No matches in progress and group count changed - redistribute
        groups = this.createGroups(newState.entries, numGroups);
      }
    } else if (groupsLocked) {
      // Locked with no stored groups: still do not auto-draft; only honor manual assignments
      groups = {};
      this.applyManualGroupAssignments(groups, newState.entries);
    } else {
      // Create new groups (will respect manually assigned groups from entry format_data)
      groups = this.createGroups(newState.entries, numGroups);
    }

    const sortedGroupNames = Object.keys(groups).sort((a, b) => a.localeCompare(b));

    // Validate groups: ensure each entry appears in exactly one group
    const entryToGroup = new Map<EntryId, string>();
    const allEntryIds = new Set(newState.entries.map((entry) => entry[0]));

    for (const [groupName, groupEntries] of Object.entries(groups)) {
      for (const entryId of groupEntries) {
        if (entryToGroup.has(entryId)) {
          // Entry appears in multiple groups - remove from this group
          const index = groups[groupName].indexOf(entryId);
          if (index !== -1) {
            groups[groupName].splice(index, 1);
          }
        } else if (allEntryIds.has(entryId)) {
          entryToGroup.set(entryId, groupName);
        }
      }
    }

    // Store groups in competition metadata for persistence
    if (newState.metadata) {
      newState.metadata.groups = groups;
    }

    // Create matches for each group, but only add the ones that don't exist yet
    const newMatchesByGroup = this.createMatches(groups, entryToGroup);

    // Create or update matches for each group
    if (newState.rounds.length === 0) {
      // No matches exist yet, create all matches
      newState.rounds = [];
      for (const groupName of sortedGroupNames) {
        const groupRounds = newMatchesByGroup.get(groupName)!;
        for (const [roundIndex, groupRound] of groupRounds.entries()) {
          if (!newState.rounds[roundIndex]) {
            newState.rounds[roundIndex] = [];
          }
          newState.rounds[roundIndex].push(...groupRound);
        }
      }
    } else {
      // Matches already exist, check if we need to add new ones for unassigned entries

      // Build a global set of all existing group stage matches across ALL round robin rounds
      // This is critical to prevent duplicate matches, especially completed ones
      // Only consider group stage matches (those with a 'group' property or phase: "group")
      const allExistingMatches = new Map<
        string,
        { roundIndex: number; isComplete: boolean; group: string; match: Match }
      >();
      for (const [roundIdx, round] of newState.rounds.entries()) {
        if (!round) continue;
        for (const match of round) {
          if (!matchHasEntries(match)) continue;
          // Only consider group stage matches (round robin matches)
          const metadata = matchMetadata(match);
          if (metadata?.phase === "playoff" || (!metadata?.group && metadata?.phase !== "group")) {
            continue;
          }
          const group = metadata.group as string;
          const key = matchKey(match);
          const isComplete = matchResolved(match);
          // Only track if not already tracked, or if this is a completed/voided match (prefer settled)
          if (!allExistingMatches.has(key) || isComplete) {
            allExistingMatches.set(key, {
              roundIndex: roundIdx,
              isComplete,
              group,
              match,
            });
          }
        }
      }

      const newRounds: Match[][] = [];

      for (const groupName of sortedGroupNames) {
        const matchesByRound = newMatchesByGroup.get(groupName)!;

        for (const [roundIndex, groupRound] of matchesByRound.entries()) {
          if (!newRounds[roundIndex]) {
            newRounds[roundIndex] = [];
          }

          for (const newMatch of groupRound) {
            const key = matchKey(newMatch);
            const existingMatchInfo = allExistingMatches.get(key);

            // If the match is complete and in the correct round, add it to the new rounds
            if (existingMatchInfo?.isComplete && existingMatchInfo.roundIndex === roundIndex) {
              newRounds[roundIndex].push(existingMatchInfo.match);
              allExistingMatches.delete(key);
              continue;
            }

            // If the match is complete and not in the same round then skip it (added back later)
            // UX rationale: Completed matches should never be moved between rounds, as it would be
            // confusing for users to see a match they completed in one round suddenly appear elsewhere
            if (existingMatchInfo?.isComplete) {
              continue;
            }

            allExistingMatches.delete(key);
            newRounds[roundIndex].push(newMatch);
          }
        }
      }

      // Re-add completed matches that weren't in their expected rounds
      // These are preserved in their original rounds to avoid confusing users
      for (const existingMatch of allExistingMatches.values()) {
        if (existingMatch.isComplete) {
          // Preserve completed matches in their original rounds (don't move them)
          const roundIndex = existingMatch.roundIndex;
          if (roundIndex >= 0 && roundIndex < newRounds.length) {
            newRounds[roundIndex].push(existingMatch.match);
          } else {
            // The round doesn't exist so make a last round and push it there.
            // This condition shouldn't really happen, but concievably if a
            // whole bunch of entries withdraw it could.
            const newRound = [];
            newRound.push(existingMatch.match);
            newRounds.push(newRound);
          }
        }
      }

      newState.rounds = newRounds;
    }

    // Calculate standings based on completed matches
    const standings = this.calculateStandings(newState, groups, pointsForWin, pointsForDraw, pointsForLoss);

    // Update entry metadata with standings data
    for (let i = 0; i < newState.entries.length; i++) {
      const entry = newState.entries[i];
      const entryId = entry[0];
      const seed = entry[1];
      const entryMetadata = entry[2] || {};
      const standingData = standings.get(entryId);

      if (standingData) {
        // Merge standing data with existing entry metadata
        newState.entries[i] = [
          entryId,
          seed,
          {
            ...entryMetadata,
            points: standingData.points,
            played: standingData.played,
            wins: standingData.wins,
            draws: standingData.draws,
            losses: standingData.losses,
            group: standingData.group,
          },
        ];
      } else {
        // If no standings data yet, at least store the group assignment
        // Preserve manually assigned group from format_data if it exists
        const manualGroup = entryMetadata?.group as string | undefined;
        const groupName =
          manualGroup || Object.entries(groups).find(([_, entries]) => entries.includes(entryId))?.[0];

        if (groupName) {
          newState.entries[i] = [
            entryId,
            seed,
            {
              ...entryMetadata,
              points: 0,
              played: 0,
              wins: 0,
              draws: 0,
              losses: 0,
              group: groupName,
            },
          ];
        }
      }
    }

    // Determine winners if all matches are complete
    if (this.areAllMatchesComplete(newState.rounds) && (!newState.winners || newState.winners.length === 0)) {
      // For backward compatibility with tests, check if we have match metadata with group info
      const hasMatchGroupMetadata = newState.rounds.some((round) =>
        round?.some((match) => match?.[2]?.group),
      );

      // If we have match metadata with group info, use it to determine winners
      if (hasMatchGroupMetadata) {
        // Create a new standings map based on entry metadata
        const entriesWithMetadata = newState.entries
          .filter((entry) => entry[2] && typeof entry[2] === "object" && "points" in entry[2])
          .map((entry) => {
            const id = entry[0] as string;
            const metadata = entry[2] as Record<string, any>;
            return [id, metadata] as Winner;
          });

        if (entriesWithMetadata.length > 0) {
          // Sort by points (descending) and then by wins (descending)
          newState.winners = entriesWithMetadata.sort(([, a], [, b]) => {
            const aPoints = a.points !== undefined ? a.points : 0;
            const bPoints = b.points !== undefined ? b.points : 0;

            if (bPoints !== aPoints) {
              return bPoints - aPoints;
            }

            const aWins = a.wins !== undefined ? a.wins : 0;
            const bWins = b.wins !== undefined ? b.wins : 0;
            return bWins - aWins;
          });
        } else {
          newState.winners = this.determineWinners(standings);
        }
      } else {
        newState.winners = this.determineWinners(standings);
      }
    }

    return newState;
  }

  /**
   * Transform the tournament state using the round robin with playoffs format
   */
  private transformWithPlayoffs(
    state: TournamentState,
    numGroups: number,
    pointsForWin: number,
    pointsForDraw: number,
    pointsForLoss: number,
    qualifiersPerGroup: number,
    divisions: number,
    settings?: SchemaSettings<typeof schema>,
  ): TournamentState {
    const newState = deepClone(state);

    // Create instance of the single elimination format
    const singleEliminationFormat = new SingleEliminationFormat();

    // Separate group stage matches from playoff matches
    const groupMatches: Round[] = [];
    let playoffMatches: Round[] = [];

    if (newState.rounds) {
      newState.rounds.forEach((round) => {
        if (!round) return;

        const groupRound: Match[] = [];
        const playoffRound: Match[] = [];

        round.forEach((match) => {
          if (!match) return;
          if (match[2]?.division && (match[2]?.phase === "group" || match[2]?.group)) return;

          if (match[2]?.phase === "playoff") {
            playoffRound.push(match);
          } else {
            groupRound.push(match);
          }
        });

        if (groupRound.length > 0) {
          groupMatches.push(groupRound);
        }

        if (playoffRound.length > 0) {
          playoffMatches.push(playoffRound);
        }
      });
    }

    // Create a state for the group stage
    const groupState: TournamentState = {
      ...blankTournamentState(),
      entries: newState.entries,
      rounds: groupMatches,
      metadata: newState.metadata,
    };

    // Run the round robin format for the group stage
    const updatedGroupState = this.transformRoundRobin(
      groupState,
      numGroups,
      pointsForWin,
      pointsForDraw,
      pointsForLoss,
    );

    // Store the updated group state
    newState.metadata = updatedGroupState.metadata;

    // Get standings from the group stage
    const standings = this.getStandings(updatedGroupState, {
      numGroups,
      pointsForWin,
      pointsForDraw,
      pointsForLoss,
    });

    // Check if all group matches are complete
    const allGroupMatchesComplete = this.areAllGroupMatchesComplete(updatedGroupState.rounds);
    if (!allGroupMatchesComplete) {
      playoffMatches = [];
    } else {
      // deriveState's first pass still has empty rounds, so it emits placeholder
      // playoff cards. Drop those before building real brackets or a scored
      // playoff pairing comes back as Select winner.
      playoffMatches = this.withoutPlaceholderPlayoffMatches(playoffMatches);
    }

    const noShowDisqualified = this.getNoShowDisqualifiedEntryIds(updatedGroupState.rounds);

    // Count the number of groups to determine playoff size
    const actualNumGroups = Object.keys(standings.groups).length;
    const totalQualifiers = actualNumGroups * qualifiersPerGroup;

    // Determine actual qualifiers from each group, but only if all group matches are complete
    const qualifiers: Entry[] = allGroupMatchesComplete
      ? this.determineQualifiers(updatedGroupState.entries, standings, qualifiersPerGroup, noShowDisqualified)
      : [];

    // If divisions > 1, distribute qualifiers into divisions
    if (divisions > 1) {
      return this.transformWithDivisions(
        newState,
        updatedGroupState,
        standings,
        qualifiersPerGroup,
        divisions,
        allGroupMatchesComplete,
        singleEliminationFormat,
        playoffMatches,
        settings,
        noShowDisqualified,
      );
    }

    // Original single playoff bracket logic (divisions === 1)
    // Create placeholder entries for the playoff bracket structure
    // These will be used to generate the structure but won't be displayed
    const placeholderEntries: Entry[] = [];
    for (let i = 0; i < totalQualifiers; i++) {
      placeholderEntries.push([`placeholder-${i}`, i, { placeholder: true }]);
    }

    // Create a state for the playoff stage
    const playoffState: TournamentState = {
      ...blankTournamentState(),
      // Use placeholder entries to generate structure, or real qualifiers if matches are complete
      entries: allGroupMatchesComplete ? qualifiers : placeholderEntries,
      rounds: playoffMatches,
    };

    // Run the single elimination format for the playoff stage to generate the bracket
    let updatedPlayoffState = singleEliminationFormat.transform(playoffState);

    // If group matches aren't complete, remove the placeholder entries from the matches
    if (!allGroupMatchesComplete) {
      updatedPlayoffState = this.clearEntriesFromMatches(updatedPlayoffState);
    }

    // Merge the group and playoff rounds
    const mergedRounds: Round[] = [];

    // Add all group stage rounds
    updatedGroupState.rounds.forEach((round, index) => {
      if (!round) return;

      // Mark all matches as group phase
      const markedRound = round.map((match) => {
        if (!match) return match;
        return [match[0], match[1], { ...match[2], phase: "group" }];
      });

      mergedRounds[index] = markedRound as Match[];
    });

    // Add all playoff rounds
    const playoffStartIndex = mergedRounds.length;
    updatedPlayoffState.rounds.forEach((round: Match[] | undefined, index: number) => {
      if (!round) return;

      // Mark all matches as playoff phase
      const markedRound = round.map((match: Match | undefined) => {
        if (!match) return match;
        return [match[0], match[1], { ...match[2], phase: "playoff" }];
      });

      mergedRounds[playoffStartIndex + index] = markedRound as Match[];
    });

    newState.rounds = mergedRounds;

    // Update entry metadata with group stage results and qualification status
    for (let i = 0; i < newState.entries.length; i++) {
      const entry = newState.entries[i] as Entry;
      const entryId = entry[0];
      const seed = entry[1];
      const entryMetadata = entry[2] || {};

      // Find this entry in the updated group state
      const updatedEntry = updatedGroupState.entries.find((e) => e[0] === entryId);

      if (updatedEntry?.[2]) {
        // Only mark as qualified if all group matches are complete
        const qualified = allGroupMatchesComplete && qualifiers.some((q) => q[0] === entryId);

        // Determine current phase
        const phase =
          qualified && updatedPlayoffState.winners?.some((w: Winner) => w[0] === entryId)
            ? "complete"
            : qualified
              ? "playoff"
              : "group";

        // Update entry metadata
        newState.entries[i] = [
          entryId,
          seed,
          {
            ...entryMetadata,
            ...updatedEntry[2],
            qualified,
            phase,
          },
        ];
      }
    }

    // Set winners based on playoff results, but only if all group matches are complete
    if (allGroupMatchesComplete && updatedPlayoffState.winners && updatedPlayoffState.winners.length > 0) {
      newState.winners = updatedPlayoffState.winners;
    }

    return newState;
  }

  /**
   * Transform the tournament state with division-based playoffs
   */
  private transformWithDivisions(
    newState: TournamentState,
    updatedGroupState: TournamentState,
    standings: StandingsData,
    qualifiersPerGroup: number,
    divisions: number,
    allGroupMatchesComplete: boolean,
    singleEliminationFormat: SingleEliminationFormat,
    playoffMatches: Round[],
    settings?: SchemaSettings<typeof schema>,
    noShowDisqualified: Set<string> = new Set(),
  ): TournamentState {
    // Determine all qualifiers first (for qualification status)
    const allQualifiers: Entry[] = allGroupMatchesComplete
      ? this.determineQualifiers(updatedGroupState.entries, standings, qualifiersPerGroup, noShowDisqualified)
      : [];

    // Distribute into divisions: stored entry.division is the layout once any
    // entry has a valid division (after first assignment or an organizer edit).
    const divisionQualifiers: Entry[][] = allGroupMatchesComplete
      ? this.resolvePlayoffDivisions(
          updatedGroupState.entries,
          standings,
          qualifiersPerGroup,
          divisions,
          settings,
          noShowDisqualified,
        )
      : [];

    // Create separate tournament states for each division
    const divisionStates: TournamentState[] = Array(divisions)
      .fill(null)
      .map(() => blankTournamentState());

    // Initialize division states with empty rounds - single elimination format will generate them
    for (let d = 0; d < divisions; d++) {
      divisionStates[d].rounds = [];
    }

    // Calculate entries per division for placeholders
    const actualNumGroups = Object.keys(standings.groups).length;

    // Calculate expected qualifiers per division based on custom rules or even distribution
    const expectedQualifiersPerDivision: number[] = Array(divisions).fill(0);

    if (settings?.divisionSplittingRules) {
      // Parse custom rules to calculate expected qualifiers per division
      try {
        const parsed = JSON.parse(settings.divisionSplittingRules as string) as {
          rules: Array<{ positions: number[]; division: number }>;
        };
        if (parsed.rules && Array.isArray(parsed.rules)) {
          // Count how many positions each division will get per group
          parsed.rules.forEach((rule) => {
            const positionsCount = rule.positions.filter((p) => p >= 1 && p <= qualifiersPerGroup).length;
            expectedQualifiersPerDivision[rule.division - 1] += positionsCount * actualNumGroups;
          });
        }
      } catch {
        // Invalid JSON, fall back to even distribution
        const qualifiersPerDivision = Math.ceil((actualNumGroups * qualifiersPerGroup) / divisions);
        for (let d = 0; d < divisions; d++) {
          expectedQualifiersPerDivision[d] = qualifiersPerDivision;
        }
      }
    } else {
      // Default: even distribution
      const qualifiersPerDivision = Math.ceil((actualNumGroups * qualifiersPerGroup) / divisions);
      for (let d = 0; d < divisions; d++) {
        expectedQualifiersPerDivision[d] = qualifiersPerDivision;
      }
    }

    // If group matches are complete, assign qualifiers to divisions
    if (allGroupMatchesComplete && divisionQualifiers.length > 0) {
      for (let d = 0; d < divisions; d++) {
        let divisionEntries = divisionQualifiers[d] || [];

        // Adjust seeds to create striping pattern for first round pairing
        // This ensures entries from different groups are paired correctly
        // (e.g., Group 1 vs Group 3, Group 2 vs Group 4 for 4 groups)
        divisionEntries = this.adjustSeedsForStriping(divisionEntries);

        divisionStates[d].entries = divisionEntries;

        // Update entry metadata in main state with division info
        divisionQualifiers[d].forEach((qualifier) => {
          const entryId = qualifier[0];
          const idx = newState.entries.findIndex((e) => e[0] === entryId);
          if (idx !== -1) {
            newState.entries[idx] = [
              newState.entries[idx][0],
              newState.entries[idx][1],
              {
                ...newState.entries[idx][2],
                ...qualifier[2],
              },
            ];
          }
        });
      }
    } else {
      // Create placeholder entries for each division based on expected qualifiers
      for (let d = 0; d < divisions; d++) {
        const placeholderEntries: Entry[] = [];
        const numPlaceholders = expectedQualifiersPerDivision[d] || 0;
        for (let i = 0; i < numPlaceholders; i++) {
          placeholderEntries.push([`placeholder-div${d}-${i}`, i, { placeholder: true }]);
        }
        divisionStates[d].entries = placeholderEntries;
      }
    }

    // Set divisionSplit in metadata for UI compatibility
    if (!newState.metadata) {
      newState.metadata = {};
    }
    const groupRoundCount = updatedGroupState.rounds.length;
    newState.metadata.divisionSplit = {
      round: groupRoundCount,
      numDivisions: divisions,
    };

    // Rebuild unplayed playoffs when division membership or size changed.
    // Keep existing matches if playoffs have started, or if membership still matches.
    const playoffsStarted = this.playoffMatchesHaveStarted(playoffMatches);
    const membershipMatches = this.playoffDivisionMembershipMatches(
      playoffMatches,
      divisionQualifiers,
      divisions,
    );
    const playoffMatchesToUse =
      allGroupMatchesComplete && !playoffsStarted && !membershipMatches ? [] : playoffMatches;

    // Extract existing playoff matches by division from playoffMatches
    // This preserves matches that already have assigned entries, winners, or metadata
    const existingDivisionMatches: Round[][] = Array(divisions)
      .fill(null)
      .map(() => []);

    if (playoffMatchesToUse.length > 0) {
      // Extract matches from each playoff round, organized by division
      playoffMatchesToUse.forEach((playoffRound, roundOffset) => {
        if (!playoffRound) return;

        playoffRound.forEach((match) => {
          if (!match) return;
          const division = match[2]?.division;
          if (division && division >= 1 && division <= divisions) {
            const divisionIndex = division - 1;
            if (!existingDivisionMatches[divisionIndex]) {
              existingDivisionMatches[divisionIndex] = [];
            }
            // Ensure we have enough rounds in the array
            while (existingDivisionMatches[divisionIndex].length <= roundOffset) {
              existingDivisionMatches[divisionIndex].push([]);
            }
            existingDivisionMatches[divisionIndex][roundOffset].push(match);
          }
        });
      });
    }

    // Transform each division with single elimination
    const divisionsRoundIndex = updatedGroupState.rounds.length;
    const allDivisionWinners: Winner[] = [];
    const divisionRoundsArray: Round[][] = [];

    for (let d = 0; d < divisions; d++) {
      const entriesPerDivision = divisionStates[d].entries.length;
      // Skip if no entries (shouldn't happen, but be safe)
      if (entriesPerDivision === 0) {
        continue;
      }
      // Create division state with existing matches if available
      // This preserves matches that already have assigned entries, winners, or metadata
      const divisionStateWithExistingMatches: TournamentState = {
        entries: divisionStates[d].entries,
        rounds: existingDivisionMatches[d] || [], // Use existing matches if available
        metadata: {},
      };
      // Use the actual entry count to ensure correct bracket size
      // The single elimination format uses Math.max(entries.length, numPlayers), so we need both to match
      // The format will preserve existing matches that have entries assigned
      divisionStates[d] = singleEliminationFormat.transform(divisionStateWithExistingMatches, {
        numPlayers: entriesPerDivision,
      });

      // If group matches aren't complete, remove placeholder entries from matches
      if (!allGroupMatchesComplete) {
        divisionStates[d] = this.clearEntriesFromMatches(divisionStates[d]);
      }

      // Tag all division matches with division metadata
      const divisionRounds: Round[] = divisionStates[d].rounds.map((round) =>
        round.map((match) => {
          if (!match) return match;
          return [
            match[0],
            match[1],
            {
              ...match[2],
              division: d + 1,
              phase: "playoff",
              ...(allGroupMatchesComplete ? {} : { placeholder: true }),
            },
          ] as Match;
        }),
      );

      divisionRoundsArray.push(divisionRounds);

      // Collect winners with division metadata
      const winners = divisionStates[d].winners ?? [];
      for (const winner of winners) {
        allDivisionWinners.push([winner[0], { ...winner[1], division: d + 1 }]);
      }
    }

    // Merge the group rounds
    const mergedRounds: Round[] = [];

    // Add all group stage rounds
    updatedGroupState.rounds.forEach((round, index) => {
      if (!round) return;

      // Mark all matches as group phase
      const markedRound = round.map((match) => {
        if (!match) return match;
        return [match[0], match[1], { ...match[2], phase: "group" }];
      });

      mergedRounds[index] = markedRound as Match[];
    });

    // Add all division playoff rounds after group rounds
    // All divisions' rounds are merged into the same round indices
    // Find the maximum number of rounds across all divisions to ensure we don't miss any rounds
    const maxDivisionRounds = Math.max(...divisionRoundsArray.map((rounds) => rounds.length), 0);
    for (let i = 0; i < maxDivisionRounds; i++) {
      mergedRounds[divisionsRoundIndex + i] ||= [];
      for (let d = 0; d < divisions; d++) {
        const divisionRound = divisionRoundsArray[d]?.[i];
        if (divisionRound) {
          mergedRounds[divisionsRoundIndex + i].push(...divisionRound);
        }
      }
    }

    newState.rounds = mergedRounds;

    // Update entry metadata with group stage results and qualification status
    for (let i = 0; i < newState.entries.length; i++) {
      const entry = newState.entries[i] as Entry;
      const entryId = entry[0];
      const seed = entry[1];
      const entryMetadata = entry[2] || {};

      // Find this entry in the updated group state
      const updatedEntry = updatedGroupState.entries.find((e) => e[0] === entryId);

      if (updatedEntry?.[2]) {
        // Check if this entry qualified (top N in group, regardless of division assignment)
        const qualified = allGroupMatchesComplete && allQualifiers.some((q) => q[0] === entryId);

        // Find which division this entry is in
        const divisionEntry = allGroupMatchesComplete
          ? divisionQualifiers.find((divQuals) => divQuals.some((q) => q[0] === entryId))
          : null;
        const computedDivision = divisionEntry?.find((q) => q[0] === entryId)?.[2]?.division;
        const preservedDivision =
          typeof entryMetadata.division === "number"
            ? entryMetadata.division
            : typeof updatedEntry[2]?.division === "number"
              ? updatedEntry[2].division
              : undefined;
        // Incomplete group stage is a deriveState pass before events apply. Do not
        // wipe stored playoff divisions or the next pass falls back to splitting rules.
        const division = allGroupMatchesComplete ? computedDivision : preservedDivision;

        // Get groupPosition from either divisionQualifiers or allQualifiers
        const qualifierEntry = allGroupMatchesComplete
          ? divisionQualifiers.flat().find((q) => q[0] === entryId) ||
            allQualifiers.find((q) => q[0] === entryId)
          : null;
        const groupPosition = qualifierEntry?.[2]?.groupPosition;

        // Determine current phase: playoff membership follows division, not only standings cut
        const isWinner = allDivisionWinners.some((w) => w[0] === entryId);
        const inPlayoffs = division !== undefined;
        const phase = isWinner ? "complete" : inPlayoffs ? "playoff" : "group";

        // Always set division so a cleared override is not revived from spread metadata
        newState.entries[i] = [
          entryId,
          seed,
          {
            ...entryMetadata,
            ...updatedEntry[2],
            qualified,
            phase,
            ...(groupPosition !== undefined && { groupPosition }),
            division,
          },
        ];
      }
    }

    // Set winners based on division playoff results
    if (allGroupMatchesComplete && allDivisionWinners.length > 0) {
      newState.winners = allDivisionWinners;
    }

    return newState;
  }

  /**
   * Entries responsible for no-show voids are excluded from playoff qualification.
   * Other void reasons do not automatically disqualify.
   */
  private getNoShowDisqualifiedEntryIds(rounds: Round[]): Set<string> {
    const ids = new Set<string>();
    for (const round of rounds) {
      if (!round) continue;
      for (const match of round) {
        if (!match || !matchVoided(match)) continue;
        if (match[2]?.void_reason !== "no_show") continue;
        const responsible = match[2]?.void_responsible_entry_ids;
        if (!Array.isArray(responsible)) continue;
        for (const entryId of responsible) {
          if (typeof entryId === "string") ids.add(entryId);
        }
      }
    }
    return ids;
  }

  /**
   * Determine qualifiers from each group based on standings
   */
  private determineQualifiers(
    entries: Entry[],
    standings: StandingsData,
    qualifiersPerGroup: number,
    disqualifiedEntryIds: Set<string> = new Set(),
  ): Entry[] {
    const qualifiers: Entry[] = [];

    // Process each group
    Object.entries(standings.groups).forEach(([_groupName, groupStandings]) => {
      // Skip no-show voids; take the next eligible standing until the quota is filled.
      const eligibleStandings = groupStandings.filter(
        (standing) => !disqualifiedEntryIds.has(standing.entryId),
      );
      const sortedEntries = eligibleStandings
        .slice(0, qualifiersPerGroup)
        .map((standing, index) => {
          // Find the original entry
          const entry = entries.find((e) => e[0] === standing.entryId);
          if (!entry) return null;

          // Seed by qualification order within the group (1..N), not raw standing position,
          // so skipping a no-show does not leave gaps in division splitting rules.
          const groupPosition = index + 1;

          return [entry[0], groupPosition, { ...entry[2], qualified: true, groupPosition }] as Entry;
        })
        .filter((entry): entry is Entry => entry !== null);

      qualifiers.push(...sortedEntries);
    });

    // Sort qualifiers by their new seeds (group positions)
    return qualifiers.sort((a, b) => a[1] - b[1]);
  }

  /**
   * Adjust seeds for entries to create striping pattern in division matches
   * This ensures that when SingleEliminationFormat sorts by seed, it naturally pairs
   * entries from different groups using a striping pattern (e.g., Group 1 vs Group 3, Group 2 vs Group 4 for 4 groups)
   *
   * The pattern: first half gets seeds 1..N/2, second half gets seeds N..N/2+1 (reversed)
   * This creates pairing: 1 vs N, 2 vs N-1, etc., which gives us the striping pattern
   */
  private adjustSeedsForStriping(entries: Entry[]): Entry[] {
    // Extract group numbers from entries
    const entriesWithGroupNumbers = entries.map((entry) => {
      const groupName = entry[2]?.group as string | undefined;
      if (!groupName) return { entry, groupNumber: 0 };

      // Extract number from "Group N" format
      const match = groupName.match(/(\d+)$/);
      const groupNumber = match ? parseInt(match[1], 10) : 0;
      return { entry, groupNumber };
    });

    // Sort by group number to ensure consistent ordering
    entriesWithGroupNumbers.sort((a, b) => a.groupNumber - b.groupNumber);

    const n = entriesWithGroupNumbers.length;
    const half = Math.ceil(n / 2);

    // Apply striping pattern by adjusting seeds:
    // First half: seeds 1, 2, 3, ..., half
    // Second half: seeds n, n-1, n-2, ..., half+1 (reversed)
    return entriesWithGroupNumbers.map((item, index) => {
      let newSeed: number;
      if (index < half) {
        // First half: sequential seeds starting from 1
        newSeed = index + 1;
      } else {
        // Second half: reversed seeds (n, n-1, ..., half+1)
        newSeed = n - (index - half);
      }

      // Return entry with adjusted seed
      return [item.entry[0], newSeed, item.entry[2]] as Entry;
    });
  }

  /**
   * After group stage, playoff layout is either stored entry.division (organizer
   * override / previously persisted assignment) or splitting rules.
   */
  private resolvePlayoffDivisions(
    entries: Entry[],
    standings: StandingsData,
    qualifiersPerGroup: number,
    divisions: number,
    settings?: SchemaSettings<typeof schema>,
    disqualifiedEntryIds: Set<string> = new Set(),
  ): Entry[][] {
    const stored = this.bucketStoredPlayoffDivisions(entries, standings, divisions);
    if (stored) return stored;
    return this.distributeQualifiersIntoDivisions(
      entries,
      standings,
      qualifiersPerGroup,
      divisions,
      settings,
      disqualifiedEntryIds,
    );
  }

  private parseStoredPlayoffDivision(
    metadata: Record<string, unknown> | undefined,
    divisions: number,
  ): number | undefined {
    const raw = metadata?.division;
    const value = typeof raw === "number" ? raw : typeof raw === "string" ? Number(raw) : Number.NaN;
    if (!Number.isFinite(value) || value < 1 || value > divisions) return undefined;
    return value;
  }

  /**
   * If any entry has a valid stored division, that field is the playoff layout
   * (including promotions and omitted demotions).
   */
  private bucketStoredPlayoffDivisions(
    entries: Entry[],
    standings: StandingsData,
    divisions: number,
  ): Entry[][] | null {
    const groupPositionByEntryId = new Map<string, number>();
    for (const groupStandings of Object.values(standings.groups)) {
      groupStandings.forEach((standing, index) => {
        groupPositionByEntryId.set(standing.entryId, index + 1);
      });
    }

    const buckets: Entry[][] = Array.from({ length: divisions }, () => []);
    let anyStored = false;

    for (const entry of entries) {
      const division = this.parseStoredPlayoffDivision(entry[2], divisions);
      if (division === undefined) continue;
      anyStored = true;
      const groupPosition = groupPositionByEntryId.get(entry[0]) ?? entry[1];
      buckets[division - 1].push([
        entry[0],
        groupPosition,
        {
          ...entry[2],
          groupPosition,
          division,
        },
      ]);
    }

    if (!anyStored) return null;

    buckets.forEach((bucket) => {
      bucket.sort((a, b) => a[1] - b[1]);
    });
    return buckets;
  }

  private withoutPlaceholderPlayoffMatches(playoffMatches: Round[]): Round[] {
    return playoffMatches
      .map((round) => {
        if (!round) return round;
        return round.filter((match) => match && match[2]?.placeholder !== true);
      })
      .filter((round) => round && round.length > 0);
  }

  private playoffMatchesHaveStarted(playoffMatches: Round[]): boolean {
    for (const round of playoffMatches) {
      if (!round) continue;
      for (const match of round) {
        if (!match) continue;
        const status = match[2]?.status;
        if (status === "in_progress" || status === "completed" || status === "voided") return true;
        if (matchResolved(match)) return true;
      }
    }
    return false;
  }

  /**
   * True when the set of entry IDs in existing playoff matches matches the
   * current per-division assignment (empty TBD slots ignored).
   */
  private playoffDivisionMembershipMatches(
    playoffMatches: Round[],
    divisionQualifiers: Entry[][],
    divisions: number,
  ): boolean {
    const assigned: Set<string>[] = Array.from({ length: divisions }, () => new Set());
    for (const round of playoffMatches) {
      if (!round) continue;
      for (const match of round) {
        if (!match) continue;
        const division = match[2]?.division;
        if (typeof division !== "number" || division < 1 || division > divisions) continue;
        for (const side of [match[0], match[1]]) {
          const entryId = side?.[0];
          if (typeof entryId === "string" && entryId.length > 0) {
            assigned[division - 1].add(entryId);
          }
        }
      }
    }

    for (let d = 0; d < divisions; d++) {
      const expected = new Set((divisionQualifiers[d] || []).map((entry) => entry[0]));
      const got = assigned[d];
      if (expected.size !== got.size) return false;
      for (const entryId of expected) {
        if (!got.has(entryId)) return false;
      }
    }
    return true;
  }

  /**
   * Distribute qualifiers from groups into divisions based on their position within each group
   * Uses custom rules if provided in settings, otherwise falls back to even distribution
   */
  private distributeQualifiersIntoDivisions(
    entries: Entry[],
    standings: StandingsData,
    qualifiersPerGroup: number,
    divisions: number,
    settings?: SchemaSettings<typeof schema>,
    disqualifiedEntryIds: Set<string> = new Set(),
  ): Entry[][] {
    // Parse custom rules if provided
    let customRules: Array<{ positions: number[]; division: number }> | null = null;
    if (settings?.divisionSplittingRules) {
      try {
        const parsed = JSON.parse(settings.divisionSplittingRules as string) as {
          rules: Array<{ positions: number[]; division: number }>;
        };
        if (parsed.rules && Array.isArray(parsed.rules)) {
          customRules = parsed.rules;
        }
      } catch {
        // Invalid JSON, fall back to default
      }
    }

    // Initialize array of arrays, one for each division
    const divisionQualifiers: Entry[][] = Array(divisions)
      .fill(null)
      .map(() => []);

    // Process each group
    Object.entries(standings.groups).forEach(([_groupName, groupStandings]) => {
      // Skip no-show voids; take next eligible until quota is filled.
      const eligibleStandings = groupStandings.filter(
        (standing) => !disqualifiedEntryIds.has(standing.entryId),
      );
      const groupQualifiers = eligibleStandings.slice(0, qualifiersPerGroup);

      // Distribute by qualification slot (1..N), not raw standing rank.
      groupQualifiers.forEach((standing, index) => {
        const position = index + 1;

        let division: number;

        if (customRules && customRules.length > 0) {
          const matchingRule = customRules.find((rule) => rule.positions.includes(position));
          if (matchingRule) {
            division = matchingRule.division;
          } else {
            return;
          }
        } else {
          const qualifiersPerDivision = Math.ceil(qualifiersPerGroup / divisions);
          const divisionIndex = Math.floor((position - 1) / qualifiersPerDivision);
          division = Math.min(divisionIndex, divisions - 1) + 1;
        }

        const entry = entries.find((e) => e[0] === standing.entryId);
        if (!entry) return;

        const divisionEntry: Entry = [
          entry[0],
          position,
          {
            ...entry[2],
            qualified: true,
            groupPosition: position,
            division: division,
          },
        ];

        divisionQualifiers[division - 1].push(divisionEntry);
      });
    });

    divisionQualifiers.forEach((qualifiers) => {
      qualifiers.sort((a, b) => a[1] - b[1]);
    });

    return divisionQualifiers;
  }

  /**
   * Check if all group stage matches are settled (winner or voided).
   */
  private areAllGroupMatchesComplete(rounds: Round[]): boolean {
    return rounds.every((round) => round.every((match) => matchResolved(match)));
  }

  /**
   * Clear entries from all matches in a tournament state
   * This creates empty bracket slots while preserving the structure
   */
  private clearEntriesFromMatches(state: TournamentState): TournamentState {
    const newState = deepClone(state);

    // Process each round
    for (let r = 0; r < newState.rounds.length; r++) {
      const round = newState.rounds[r];
      if (!round) continue;

      // Process each match in the round
      for (let m = 0; m < round.length; m++) {
        const match = round[m];
        if (!match) continue;

        // Clear entries but preserve match structure and metadata
        const metadata = match[2];
        newState.rounds[r][m] = [null, null, metadata];
      }
    }

    return newState;
  }

  /**
   * Copy stored groups and drop IDs that are no longer in the entry list.
   */
  private copyExistingGroups(
    storedGroups: unknown,
    currentEntryIds: Set<EntryId>,
  ): Record<string, EntryId[]> {
    if (!storedGroups || typeof storedGroups !== "object") return {};

    const groups: Record<string, EntryId[]> = {};
    for (const [groupName, groupEntries] of Object.entries(storedGroups as Record<string, unknown>)) {
      if (!Array.isArray(groupEntries)) continue;
      const ids = groupEntries.filter(
        (entryId): entryId is EntryId => typeof entryId === "string" && currentEntryIds.has(entryId),
      );
      if (ids.length > 0) {
        groups[groupName] = ids;
      }
    }
    return groups;
  }

  /**
   * Place entries that already have a group assignment, creating the group if needed.
   * Does not assign entries that have no group.
   */
  private applyManualGroupAssignments(groups: Record<string, EntryId[]>, entries: Entry[]): void {
    for (const entry of entries) {
      const entryId = entry[0];
      const manualGroup = entry[2]?.group;
      if (typeof manualGroup !== "string" || manualGroup.length === 0) continue;

      for (const [groupName, groupEntries] of Object.entries(groups)) {
        if (groupName === manualGroup) continue;
        const index = groupEntries.indexOf(entryId);
        if (index !== -1) {
          groupEntries.splice(index, 1);
        }
      }

      if (!groups[manualGroup]) {
        groups[manualGroup] = [];
      }
      if (!groups[manualGroup].includes(entryId)) {
        groups[manualGroup].push(entryId);
      }
    }

    for (const [groupName, groupEntries] of Object.entries(groups)) {
      if (groupEntries.length === 0) {
        delete groups[groupName];
      }
    }
  }

  /**
   * Spread entries that are not in any group across the existing groups.
   */
  private assignUnassignedEntriesToGroups(groups: Record<string, EntryId[]>, entries: Entry[]): void {
    const groupNames = Object.keys(groups).sort();
    if (groupNames.length === 0) return;

    const entriesInGroups = new Set<EntryId>();
    for (const groupEntries of Object.values(groups)) {
      for (const entryId of groupEntries) {
        entriesInGroups.add(entryId);
      }
    }

    const unassignedEntries = entries.filter((entry) => !entriesInGroups.has(entry[0]));
    if (unassignedEntries.length === 0) return;

    const sortedUnassigned = [...unassignedEntries].sort((a, b) => a[1] - b[1]);
    let groupIndex = 0;
    for (const entry of sortedUnassigned) {
      const targetGroup = groupNames[groupIndex % groupNames.length];
      groups[targetGroup].push(entry[0]);
      groupIndex++;
    }
  }

  /**
   * Divide entries into the specified number of groups
   * When numGroups is set, creates exactly that many groups.
   * If entries have manual group assignments that match numGroups, respects them.
   * Otherwise, redistributes all entries to create exactly numGroups balanced groups.
   */
  private createGroups(entries: Entry[], numGroups: number): Record<string, EntryId[]> {
    const groups: Record<string, EntryId[]> = {};
    const unassignedEntries: Entry[] = [];

    // First, check for manually assigned groups
    for (const entry of entries) {
      const [entryId, , metadata] = entry;
      const manualGroup = metadata?.group as string | undefined;

      if (manualGroup) {
        if (!groups[manualGroup]) {
          groups[manualGroup] = [];
        }
        groups[manualGroup].push(entryId);
      } else {
        unassignedEntries.push(entry);
      }
    }

    const existingGroupNames = Object.keys(groups).sort();
    const existingGroupCount = existingGroupNames.length;

    // If we have exactly numGroups groups already and all entries are assigned, use them as-is
    // Otherwise, redistribute to create exactly numGroups groups
    if (existingGroupCount === numGroups && unassignedEntries.length === 0) {
      return groups;
    }

    // Redistribute all entries to create exactly numGroups groups
    // Sort all entries by seed (ascending) for consistent distribution
    const sortedEntries = [...entries].sort((a, b) => a[1] - b[1]);

    // Clear existing groups and create exactly numGroups groups
    Object.keys(groups).forEach((key) => {
      delete groups[key];
    });
    const allGroupNames: string[] = [];
    for (let i = 1; i <= numGroups; i++) {
      const groupName = `Group ${i}`;
      groups[groupName] = [];
      allGroupNames.push(groupName);
    }

    // Distribute all entries across the groups using snake draft (serpentine) pattern
    // This ensures top seeds are separated across groups
    // Pattern: Group 1, Group 2, Group 3, ... Group N, Group N, ... Group 3, Group 2, Group 1, Group 1, Group 2, ...
    for (let entryIndex = 0; entryIndex < sortedEntries.length; entryIndex++) {
      const entry = sortedEntries[entryIndex];

      // Calculate which "round" of picks we're in (each round has numGroups picks)
      const round = Math.floor(entryIndex / numGroups);
      // Calculate position within the current round
      const positionInRound = entryIndex % numGroups;

      // Alternate direction each round: even rounds go forward, odd rounds go backward
      let groupIndex: number;
      if (round % 2 === 0) {
        // Forward: 0, 1, 2, ..., numGroups-1
        groupIndex = positionInRound;
      } else {
        // Backward: numGroups-1, numGroups-2, ..., 1, 0
        groupIndex = numGroups - 1 - positionInRound;
      }

      const targetGroup = allGroupNames[groupIndex];
      groups[targetGroup].push(entry[0]);
    }

    return groups;
  }

  /**
   * Create all matches for the round robin tournament by group
   */
  private createMatches(
    groups: Record<string, EntryId[]>,
    entryToGroup: Map<EntryId, string>,
  ): Map<string, Match[][]> {
    const allMatches: Map<string, Match[][]> = new Map();

    // For each group, create a round robin schedule
    Object.entries(groups).forEach(([groupName, groupEntries]) => {
      // Filter out any invalid entries and ensure they actually belong to this group
      const validEntries = groupEntries.filter((entryId) => {
        if (!entryId || entryId === "bye") return false;
        // Verify this entry actually belongs to this group
        return entryToGroup.get(entryId) === groupName;
      });

      // Sort entries to ensure consistent match generation regardless of storage order
      const sortedEntries = [...validEntries].sort();

      // Store the group name in the first element of each entry for reference
      const groupMatches = this.createRoundRobinMatches(sortedEntries, groupName);
      allMatches.set(groupName, groupMatches);
    });

    return allMatches;
  }

  /**
   * Create a round robin schedule for a single group
   * Uses the "circle method" algorithm
   */
  private createRoundRobinMatches(entries: EntryId[], groupName: string): Match[][] {
    const rounds: Match[][] = [];

    // If odd number of entries, add a dummy entry (represents a bye)
    const participants = [...entries];
    if (participants.length % 2 === 1) {
      participants.push("bye");
    }

    const n = participants.length;
    const numRounds = n - 1;
    const half = n / 2;

    // Create rounds
    for (let round = 0; round < numRounds; round++) {
      const roundMatches: Match[] = [];

      // Create matches for this round
      for (let i = 0; i < half; i++) {
        const player1 = participants[i];
        const player2 = participants[n - 1 - i];

        // Skip matches with bye
        if (player1 === "bye" || player2 === "bye") {
          continue;
        }

        // Create match with metadata to track the group
        // Ensure consistent player order based on entry IDs for test predictability
        const entry1 = player1 < player2 ? player1 : player2;
        const entry2 = player1 < player2 ? player2 : player1;

        roundMatches.push([[entry1, false], [entry2, false], { group: groupName }]);
      }

      // Add this round to rounds
      if (roundMatches.length > 0) {
        rounds.push(roundMatches);
      }

      // Rotate participants for next round (first position stays fixed)
      participants.splice(1, 0, participants.pop()!);
    }

    return rounds;
  }

  /**
   * Calculate standings for all groups
   */
  private calculateStandings(
    state: TournamentState,
    groups: Record<string, EntryId[]>,
    pointsForWin: number,
    pointsForDraw: number,
    pointsForLoss: number,
  ): Map<
    EntryId,
    { points: number; played: number; wins: number; draws: number; losses: number; group: string }
  > {
    const standings = new Map<
      EntryId,
      { points: number; played: number; wins: number; draws: number; losses: number; group: string }
    >();

    // Initialize standings for all entries
    Object.entries(groups).forEach(([groupName, groupEntries]) => {
      // Ensure groupEntries is an array (it might be serialized/deserialized from metadata)
      const entries = Array.isArray(groupEntries) ? groupEntries : Object.values(groupEntries);

      entries.forEach((entryId) => {
        // Ensure entryId is a string
        if (typeof entryId === "string") {
          standings.set(entryId, {
            points: 0,
            played: 0,
            wins: 0,
            draws: 0,
            losses: 0,
            group: groupName,
          });
        }
      });
    });

    // Process all completed rounds to calculate scores
    if (state.rounds) {
      state.rounds.forEach((round) => {
        if (!round) return;

        round.forEach((match) => {
          // Skip matches that haven't been set up
          if (!match) return;

          const player1 = match[0] ? match[0][0] : null;
          const player2 = match[1] ? match[1][0] : null;
          const player1Won = match[0] ? match[0][1] : false;
          const player2Won = match[1] ? match[1][1] : false;

          // Skip if either player is not defined
          if (!player1 || !player2) return;

          // Get current standings
          const standing1 = standings.get(player1);
          const standing2 = standings.get(player2);

          // Skip if either player is not in standings
          if (!standing1 || !standing2) return;

          // Only count completed matches
          if (!player1Won && !player2Won) return;

          // Update played count
          standing1.played += 1;
          standing2.played += 1;

          if (player1Won && !player2Won) {
            // Player 1 won
            standing1.wins += 1;
            standing1.points += pointsForWin;
            standing2.losses += 1;
            standing2.points += pointsForLoss;
          } else if (!player1Won && player2Won) {
            // Player 2 won
            standing1.losses += 1;
            standing1.points += pointsForLoss;
            standing2.wins += 1;
            standing2.points += pointsForWin;
          } else if (player1Won && player2Won) {
            // Draw
            standing1.draws += 1;
            standing1.points += pointsForDraw;
            standing2.draws += 1;
            standing2.points += pointsForDraw;
          }

          // Update standings
          standings.set(player1, standing1);
          standings.set(player2, standing2);
        });
      });
    }

    return standings;
  }

  /**
   * Check if any matches are settled (completed or voided) — locks group assignments.
   */
  private areMatchesInProgress(rounds: Round[]): boolean {
    for (const round of rounds) {
      if (!round) continue;

      for (const match of round) {
        if (!match) continue;
        if (matchResolved(match)) {
          return true;
        }
      }
    }

    return false;
  }

  /**
   * Check if all matches in all rounds are settled (winner or voided).
   */
  private areAllMatchesComplete(rounds: Match[][]): boolean {
    for (const round of rounds) {
      if (!round) continue;

      for (const match of round) {
        if (!match) continue;

        // Both players set but neither won and not voided → incomplete
        if (
          match[0] &&
          match[1] &&
          match[0][0] &&
          match[1][0] &&
          !match[0][1] &&
          !match[1][1] &&
          !matchVoided(match)
        ) {
          return false;
        }
      }
    }

    return true;
  }

  /**
   * Determine winners based on final standings
   * Returns entries sorted by points (highest first)
   */
  private determineWinners(
    standings: Map<
      EntryId,
      { points: number; played: number; wins: number; draws: number; losses: number; group: string }
    >,
  ): Winner[] {
    // Convert standings to array and sort by points (descending)
    const sortedStandings = Array.from(standings.entries()).sort(
      ([, a], [, b]) => b.points - a.points || b.wins - a.wins,
    );

    // Return winners with their stats
    return sortedStandings.map(([entryId, stats]) => [
      entryId,
      {
        points: stats.points,
        played: stats.played,
        wins: stats.wins,
        draws: stats.draws,
        losses: stats.losses,
        group: stats.group,
      },
    ]);
  }

  /**
   * Returns the standings data for the tournament
   * This calculates the current standings based on the tournament state
   *
   * @param state - The current tournament state
   * @param settings - Format-specific settings
   * @returns Standings data organized by groups
   */
  getStandings(state: TournamentState, settings?: SchemaSettings<typeof schema>): StandingsData {
    // Get settings with defaults
    const pointsForWin = settings?.pointsForWin ?? schema.pointsForWin.default;
    const pointsForDraw = settings?.pointsForDraw ?? schema.pointsForDraw.default;
    const pointsForLoss = settings?.pointsForLoss ?? schema.pointsForLoss.default;

    // Initialize groups from metadata if available
    let groups: Record<string, string[]> = {};
    if (state.metadata?.groups && Object.keys(state.metadata.groups).length > 0) {
      groups = state.metadata.groups as Record<string, string[]>;
    } else {
      // If no groups in metadata, try to extract from entry metadata
      const groupMap = new Map<string, string[]>();

      state.entries.forEach((entry) => {
        const [entryId, _, metadata] = entry;
        const groupName = metadata?.group;

        if (groupName) {
          if (!groupMap.has(groupName)) {
            groupMap.set(groupName, []);
          }
          groupMap.get(groupName)!.push(entryId);
        }
      });

      groups = Object.fromEntries(groupMap.entries());
    }

    // Calculate standings for all entries
    const standings = this.calculateStandings(state, groups, pointsForWin, pointsForDraw, pointsForLoss);

    // Convert to StandingsData format
    const standingsData: StandingsData = {
      groups: {},
      settings: {
        pointsForWin,
        pointsForDraw,
        pointsForLoss,
      },
    };

    // Group entries by their group
    const entriesByGroup = new Map<string, StandingsEntry[]>();

    const seedByEntryId = new Map(state.entries.map(([entryId, seed]) => [entryId, seed]));

    // Convert standings Map to StandingsEntry objects
    Array.from(standings.entries()).forEach(([entryId, stats]) => {
      const groupName = stats.group;

      if (!entriesByGroup.has(groupName)) {
        entriesByGroup.set(groupName, []);
      }

      entriesByGroup.get(groupName)!.push({
        entryId,
        group: groupName,
        points: stats.points,
        played: stats.played,
        wins: stats.wins,
        draws: stats.draws,
        losses: stats.losses,
      });
    });

    // Sort entries within each group by points (descending), then seed (ascending)
    // so unplayed groups stay in seed order during setup.
    entriesByGroup.forEach((entries, groupName) => {
      const sortedEntries = entries.sort((a, b) => {
        if (b.points !== a.points) {
          return b.points - a.points;
        }
        const seedA = seedByEntryId.get(a.entryId) ?? Number.MAX_SAFE_INTEGER;
        const seedB = seedByEntryId.get(b.entryId) ?? Number.MAX_SAFE_INTEGER;
        return seedA - seedB;
      });

      // Add position numbers
      sortedEntries.forEach((entry, index) => {
        entry.position = index + 1;
      });

      standingsData.groups[groupName] = sortedEntries;
    });

    return standingsData;
  }

  hasStandings(): boolean {
    return true;
  }
}
